import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  DEFAULT_GLOBAL_MAP_ZOOM,
  panToGlobalMap,
} from '@rsos/utils/panToGlobalMap';
import { useResourceRequestRegistryLists } from './adapters';

/**
 * Displays ETL-based status banners for resource requests associated with the
 * currently selected call.
 */
function ResourceRequestStatusBanner(): JSX.Element | null {
  const dispatch = useDispatch();
  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const listSlices = useResourceRequestRegistryLists();

  const bannerNodes = useMemo(() => {
    if (!selectedCallerID) {
      return [];
    }

    return listSlices.flatMap(({ adapter, requests }) => {
      const BannerComponent = adapter.BannerComponent;
      if (!BannerComponent) {
        return [];
      }

      return requests
        .filter(request => adapter.getCallerId(request) === selectedCallerID)
        .map(request => (
          <Fragment key={`${adapter.id}-${adapter.getId(request)}`}>
            <BannerComponent
              request={request}
              onOpen={() => {
                const pickupLocation = adapter.getPickupLocation?.(request);
                if (pickupLocation) {
                  panToGlobalMap(
                    pickupLocation.latitude,
                    pickupLocation.longitude,
                    DEFAULT_GLOBAL_MAP_ZOOM,
                  );
                }

                dispatch(setSelectedRequest(adapter.getId(request)));
                dispatch(openResourcePanel());
                dispatch(setResourcePanelView(adapter.id));
              }}
            />
          </Fragment>
        ));
    });
  }, [dispatch, listSlices, selectedCallerID]);

  if (bannerNodes.length === 0) {
    return null;
  }

  return <>{bannerNodes}</>;
}

export default ResourceRequestStatusBanner;
